import React from "react";

/* w4t-convoy, w4t-wheel and w4t-bob-a/b/c live in this stylesheet and are
 * reused rather than redefined — see the deviations note at the top of
 * header-convoy.css. Imported explicitly so the dependency is real, not a
 * load-order accident. */
import "./independence-day-navbar.css";
import "./header-convoy.css";

/* ------------------------------------------------------------------------
 * The header convoy, diorama edition.
 *
 * Seven vehicles authored in a 44-unit-tall viewBox and rendered at 38.7px
 * (88%), travelling right to left toward the wordmark on a painted road:
 * roadside scenery, balloons, golden-hour light, marigold petals, and per
 * vehicle a headlight cone, tail lamp, exhaust plume, tyre dust, a pinging
 * roof GPS tracker and a green breadcrumb trail.
 *
 *   134 + 51 + 116 + 76 + 84 + 53 + 77 = 591   seven bodies at 0.88
 *   7 x 300                            = 2100  the gap after each
 *                                      = 2691  one set
 *
 * The track holds two identical sets and translates by exactly one set
 * width, so the loop has no seam. CHANGE A WIDTH OR THE GAP AND YOU MUST
 * RECOMPUTE 2691 — and the wheel durations with it:
 *
 *   wheelDuration(r) = (2(pi) x r x 0.88 / 2691) x seconds
 *     at 26s:  r 6.8 -> .363s   r 6.5 -> .347s   r 5.5 -> .294s
 *
 * Strip geometry is load-bearing: 44px tall, track at bottom 3px. That puts
 * each svg's top at ~2.3px (no roof clipped) and the artwork's y=40 contact
 * line at ~37.5px from the top, inside the 32->44px asphalt band, so the
 * wheels sit ON the road. Change either number and one of those breaks.
 *
 * Four rules that are easy to get wrong:
 *   1. The mirror lives on the <svg>; the suspension bob on the wrapping
 *      <span>. Both on one element and the two transforms fight.
 *   2. Mirroring reverses the wheel rotation, which is correct for leftward
 *      travel. Do not "fix" it.
 *   3. Gradient/clipPath ids must be unique per rendered vehicle (the uid
 *      prop) — duplicate ids all resolve to the first instance's defs.
 *   4. Never apply a colour->gradient substitution across a whole file;
 *      scope it to these svgs or unrelated artwork drops out as invalid
 *      paint.
 *
 * RECONSTRUCTED SECTIONS — the brief this implements arrived truncated at
 * its 50,000-character paste limit, mid-way through the Auto's <defs>. The
 * Auto and Taxi festive layers, and the Roadside / Balloons / Petals
 * scenery, are therefore reconstructed here: the vehicle bodies are the
 * verbatim artwork this repo has always used, and the festive parameters
 * follow the exact per-vehicle arithmetic of the five vehicles that did
 * arrive (sun sweep: duration cycles 5.5/6.8/8.1/9.4s, delay = i x 0.9s;
 * ping: duration cycles 2.20/2.55/2.90/3.25s, delay = i x 0.41s, second arc
 * +0.22s). The scenery is authored to the brief's layer spec and keyframes.
 * Paste the missing tail of the brief to swap in the originals.
 *
 * Entirely decorative: aria-hidden, no focusable children.
 * ---------------------------------------------------------------------- */

const SCALE = 0.88;
const SET = 2691;

const VEHICLE_STYLE = {
    display: "block",
    transform: "scaleX(-1)", // artwork is drawn facing right
    filter: "drop-shadow(0 3px 3px rgba(31,31,31,.18))", // contact shadow
};

/* ---------------------------------------------------------------- defs */

/* Every vehicle carries the same eight body gradients, a glass gradient, a
 * sunlight gradient and its own clip path. Factored so 7 vehicles x 2 sets
 * cannot drift apart — the ids and stops are byte-identical to the brief's
 * inline versions. */
const BODY_GRADS = [
    ["b0", ["#f2f5f8", "#eef2f6", "#EDF1F5", "#afb2b5"]],
    ["b1", ["#78b172", "#4d9845", "#3E8F35", "#2e6a27"]],
    ["b2", ["#d3746b", "#c5493c", "#C0392B", "#8e2a20"]],
    ["b3", ["#f9faf9", "#f7f9f8", "#F6F8F7", "#b6b8b7"]],
    ["b4", ["#6c9acb", "#3e7bbb", "#2D6FB5", "#215286"]],
    ["b5", ["#fafcfc", "#f9fafb", "#F8FAFB", "#b8b9ba"]],
    ["b6", ["#f6d46e", "#f3c741", "#F2C230", "#b39024"]],
    ["b7", ["#579a51", "#227a1b", "#0F6E07", "#0b5105"]],
];

const Defs = ({ uid, clip }) => (
    <defs>
        <clipPath id={`w4tc-${uid}`}>{clip}</clipPath>
        <linearGradient id={`w4tsh-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#FFFFFF" stopOpacity="0" />
            <stop offset=".5" stopColor="#FFFFFF" stopOpacity=".62" />
            <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        {BODY_GRADS.map(([k, [s0, s1, s2, s3]]) => (
            <linearGradient key={k} id={`w4t${k}-${uid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={s0} />
                <stop offset=".34" stopColor={s1} />
                <stop offset=".62" stopColor={s2} />
                <stop offset="1" stopColor={s3} />
            </linearGradient>
        ))}
        <linearGradient id={`w4tg-${uid}`} x1="0" y1="0" x2=".25" y2="1">
            <stop offset="0" stopColor="#F2F8FE" />
            <stop offset=".46" stopColor="#CFE4F5" />
            <stop offset="1" stopColor="#9FBDD6" />
        </linearGradient>
    </defs>
);

/* ------------------------------------------------------------- fittings */

/* Tread notches, a 3-spoke hub and an off-centre valve dot are what make
 * rotation legible at ~13px diameter — a plain dark disc looks static
 * however fast you spin it. The variant picks which --w4t-wheel-* duration
 * applies; fallbacks are correct at the default 26s. */
const Wheel = ({ cx, cy = 33.5, r = 6.8, hub = 2.8 }) => {
    const v = r >= 6.7 ? "a" : r >= 6.2 ? "b" : "c";
    const fallback = r >= 6.7 ? 0.363 : r >= 6.2 ? 0.347 : 0.294;
    const ri = r - 2.3;
    const ro = r - 0.6;
    const sl = hub * 1.55;
    const spokes = [0, 60, 120]
        .map((deg) => {
            const a = (deg * Math.PI) / 180;
            const dx = Math.cos(a) * sl;
            const dy = Math.sin(a) * sl;
            return `M${cx - dx} ${cy - dy} L${cx + dx} ${cy + dy}`;
        })
        .join(" ");
    const va = (38 * Math.PI) / 180;
    const vr = (r + hub) / 2;

    return (
        <g
            style={{
                transformOrigin: `${cx}px ${cy}px`,
                animation: `w4t-wheel var(--w4t-wheel-${v}, ${fallback}s) linear infinite`,
            }}
        >
            <circle cx={cx} cy={cy} r={r} fill="#23272C" />
            <path
                d={`M${cx} ${cy - ro} V${cy - ri} M${cx} ${cy + ri} V${cy + ro} M${cx - ro} ${cy} H${cx - ri} M${cx + ri} ${cy} H${cx + ro}`}
                stroke="#4E555D"
                strokeWidth="1.2"
                strokeLinecap="round"
            />
            <circle cx={cx + Math.cos(va) * vr} cy={cy - Math.sin(va) * vr} r="0.75" fill="#E8DFC8" />
            <circle cx={cx} cy={cy} r={hub} fill="#C9D0D8" />
            <path d={spokes} stroke="#868E97" strokeWidth="0.9" strokeLinecap="round" />
        </g>
    );
};

const RoofFlag = ({ x, y }) => (
    <>
        <rect x={x} y={y} width="1.2" height="7" fill="#8D97A3" />
        <rect x={x + 1.2} y={y} width="9" height="2" fill="#FF9933" />
        <rect x={x + 1.2} y={y + 2} width="9" height="2" fill="#FFFFFF" />
        <rect x={x + 1.2} y={y + 4} width="9" height="2" fill="#138808" />
    </>
);

const Shadow = ({ cx, rx }) => (
    <ellipse cx={cx} cy="41" rx={rx} ry="2" fill="#1F1F1F" opacity=".13" />
);

/* Sunlight sweep, clipped to the bodywork. Painted last inside each svg.
 *
 * ONE FIX over the brief's markup: it animated the clip-carrying <g>, but a
 * clip-path travels WITH the element it is on — group and clip translate
 * together, so the off-canvas rect and the bodywork shape never intersect
 * and the sweep paints nothing, ever. The animation belongs on the rect,
 * moving inside the static clip. */
const Sun = ({ uid, dur, delay }) => (
    <g clipPath={`url(#w4tc-${uid})`}>
        <rect
            x="-44"
            y="0"
            width="30"
            height="44"
            fill={`url(#w4tsh-${uid})`}
            style={{ animation: `w4t-sun ${dur}s linear ${delay}s infinite` }}
        />
    </g>
);

/* Roof GPS tracker: box, pinging LED, two expanding signal arcs.
 *
 * ONE FIX over the brief's markup: it set transform-origin on the group
 * wrapping the arcs, but w4t-arc animates transform: scale on the PATHS —
 * and transform-origin does not inherit. Without their own origin, SVG
 * paths scale about the viewBox default, which for a ~5px arc parked at the
 * roofline means it lunges across the vehicle every ping. Each path now
 * carries the origin itself.
 *
 * `rise` scales how far the arcs climb. At 1 (the default) the offsets are
 * the brief's exactly. The Bike's tracker sits on the helmet at y=3.8, where
 * the brief's full-height arcs land entirely ABOVE the viewBox and are
 * clipped invisible — it passes .55, which keeps both arcs in frame. */
const Tracker = ({ cx, y, dur, delay, rise = 1 }) => {
    const oy = y + 1.6;
    const a1 = y - 2.6 * rise;
    const a2 = y - 4.6 * rise;
    const origin = { transformOrigin: `${cx}px ${oy}px` };

    return (
        <g style={origin}>
            <rect x={cx - 2.2} y={y} width="4.4" height="2.4" rx=".8" fill="#2F3A44" />
            <circle
                cx={cx}
                cy={y - 0.3}
                r=".9"
                fill="#4FAE43"
                style={{ animation: `w4t-ping ${dur}s ease-in-out ${delay}s infinite` }}
            />
            <g fill="none" stroke="#4FAE43" strokeWidth="1.1" strokeLinecap="round">
                <path
                    d={`M${cx - 3.1} ${a1} Q${cx} ${a1 - 2.2 * rise} ${cx + 3.1} ${a1}`}
                    style={{ ...origin, animation: `w4t-arc ${dur}s ease-out ${delay}s infinite` }}
                />
                <path
                    d={`M${cx - 5.2} ${a2} Q${cx} ${a2 - 3.4 * rise} ${cx + 5.2} ${a2}`}
                    style={{
                        ...origin,
                        animation: `w4t-arc ${dur}s ease-out ${(delay + 0.22).toFixed(2)}s infinite`,
                    }}
                />
            </g>
        </g>
    );
};

/* ------------------------------------------------------------- vehicles */

const Truck = ({ uid }) => (
    <svg width="134" height="38.7" viewBox="0 0 152 44" aria-hidden="true" focusable="false" style={VEHICLE_STYLE}>
        <Defs uid={uid} clip={<rect x="3" y="9" width="93" height="22" rx="2" />} />
        <Shadow cx={76} rx={70} />
        <rect x="3" y="9" width="93" height="22" rx="2" fill={`url(#w4tb0-${uid})`} />
        <rect x="3" y="9" width="93" height="4" fill="#DBE2EA" />
        <g stroke="#D2DAE2" strokeWidth="1">
            <path d="M17 10 V30" />
            <path d="M31 10 V30" />
            <path d="M45 10 V30" />
            <path d="M59 10 V30" />
            <path d="M73 10 V30" />
            <path d="M87 10 V30" />
        </g>
        <rect x="3" y="30" width="95" height="3.5" fill="#8D97A3" />
        <path
            d="M99 33.5 L99 12 Q99 9 103 9 L123 9 Q127 9 129 12.5 L134.5 21 L143 22.8 Q147 23.6 147 27.5 L147 33.5 Z"
            fill={`url(#w4tb1-${uid})`}
        />
        <path d="M99 27 L147 27 L147 33.5 L99 33.5 Z" fill="#2F7228" />
        <path d="M118.5 11.5 L124 11.5 L129.5 20 L118.5 20 Z" fill={`url(#w4tg-${uid})`} />
        <rect x="101" y="11.5" width="15" height="8.5" rx="1" fill={`url(#w4tg-${uid})`} opacity=".9" />
        <rect x="143" y="24.5" width="4" height="3" rx="1.2" fill="#FFE7A6" />
        <rect x="3" y="24" width="4" height="3" rx="1" fill="#E8564A" />
        <RoofFlag x={105} y={2} />
        {/* tricolour livery along the cargo box */}
        <g>
            <rect x="6" y="26.4" width="87" height="1.5" fill="#FF9933" />
            <rect x="6" y="27.9" width="87" height="1.5" fill="#FFFFFF" />
            <rect x="6" y="29.4" width="87" height="1.5" fill="#138808" />
        </g>
        <ellipse cx="22" cy="33" rx="8.4" ry="3.4" fill="#1F1F1F" opacity=".2" />
        <ellipse cx="110" cy="33" rx="8.4" ry="3.4" fill="#1F1F1F" opacity=".2" />
        <Wheel cx={22} />
        <Wheel cx={37} />
        <Wheel cx={110} />
        <Wheel cx={139} />
        {/* marigold garland over the nose */}
        <g opacity=".95">
            <path d="M130.5 21.8 Q134.5 27.2 139 27.6 Q143.5 28 146.6 25.6" fill="none" stroke="#E0821F" strokeWidth=".8" />
            <circle cx="131.4" cy="23.1" r="1.5" fill="#FF9933" />
            <circle cx="133.8" cy="25.4" r="1.7" fill="#F2A03D" />
            <circle cx="136.8" cy="26.9" r="1.7" fill="#FFC46B" />
            <circle cx="140" cy="27.4" r="1.7" fill="#FF9933" />
            <circle cx="143" cy="27" r="1.6" fill="#F2A03D" />
            <circle cx="145.3" cy="26" r="1.5" fill="#FFC46B" />
            <circle cx="146.8" cy="24.4" r="1.3" fill="#FF9933" />
        </g>
        <Sun uid={uid} dur={5.5} delay={0} />
        <Tracker cx={50} y={7} dur={2.2} delay={0} />
    </svg>
);

const Bike = ({ uid }) => (
    <svg width="51" height="38.7" viewBox="0 0 58 44" aria-hidden="true" focusable="false" style={VEHICLE_STYLE}>
        <Defs
            uid={uid}
            clip={<path d="M17 26 Q17 22 22 21 L31 19.5 Q36 19 38 22 L38.5 25 L30 27 L21 27.5 Z" />}
        />
        <Shadow cx={29} rx={22} />
        <path d="M45 33.5 L41.5 22" stroke="#5A626B" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M13 33.5 L24 30.5" stroke="#5A626B" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M22 27 L31 27 L31 32 L23 32 Z" fill="#4A5158" />
        <path d="M17 26 Q17 22 22 21 L31 19.5 Q36 19 38 22 L38.5 25 L30 27 L21 27.5 Z" fill={`url(#w4tb2-${uid})`} />
        <path d="M12 25.5 Q11 28 14 28.5 L21 27.5 L20 24.5 Z" fill="#1E2328" />
        <path d="M38 20.5 L45 18.5" stroke="#2B3037" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="43.5" cy="21.5" r="2.4" fill="#FFE08A" />
        <path d="M27 15 Q24 19 23.5 25 L29 25.5 Q32 20 32.5 15 Z" fill="#1A3A8F" />
        <path d="M31 17 L40 20.5" stroke="#1A3A8F" strokeWidth="2.6" strokeLinecap="round" />
        <path d="M27 25 L26 31 L30 31.5" stroke="#2F3844" strokeWidth="2.6" strokeLinecap="round" fill="none" />
        <circle cx="29.5" cy="11" r="4.4" fill="#FF7722" />
        <path d="M32 9.6 L34.2 10.6 L33.8 13 L31.6 13 Z" fill="#2B3037" opacity=".5" />
        <ellipse cx="13" cy="33" rx="7.6" ry="3" fill="#1F1F1F" opacity=".18" />
        <ellipse cx="45" cy="33" rx="7.6" ry="3" fill="#1F1F1F" opacity=".18" />
        <Wheel cx={13} r={6.5} hub={2.6} />
        <Wheel cx={45} r={6.5} hub={2.6} />
        <Sun uid={uid} dur={6.8} delay={0.9} />
        {/* the rider's tricolour scarf, streaming behind */}
        <g style={{ transformOrigin: "26px 15px", animation: "w4t-scarf 1.5s ease-in-out infinite" }}>
            <path d="M26.5 14.6 Q22 15.6 18.6 13.4 Q21.6 17 26 16.6 Z" fill="#FF9933" />
            <path d="M26.5 16.2 Q21.4 17.6 17.4 15.8 Q21 19.2 26.2 18.2 Z" fill="#FFFFFF" />
            <path d="M26.4 17.8 Q21.8 19.4 18.4 18.4 Q21.8 21 26.2 19.8 Z" fill="#138808" />
        </g>
        <Tracker cx={29} y={3.8} dur={2.55} delay={0.41} rise={0.55} />
    </svg>
);

const Bus = ({ uid }) => (
    <svg width="116" height="38.7" viewBox="0 0 132 44" aria-hidden="true" focusable="false" style={VEHICLE_STYLE}>
        <Defs uid={uid} clip={<path d="M3 33.5 L3 12 Q3 8 8 8 L119 8 Q126 8 128 12.5 L129 33.5 Z" />} />
        <Shadow cx={66} rx={60} />
        <path d="M3 33.5 L3 12 Q3 8 8 8 L119 8 Q126 8 128 12.5 L129 33.5 Z" fill={`url(#w4tb3-${uid})`} />
        <rect x="3" y="12.5" width="126" height="10" fill="#2E3A44" />
        <g fill={`url(#w4tg-${uid})`}>
            <rect x="6" y="14" width="16" height="7.5" rx="1" />
            <rect x="25" y="14" width="16" height="7.5" rx="1" />
            <rect x="44" y="14" width="16" height="7.5" rx="1" />
            <rect x="63" y="14" width="16" height="7.5" rx="1" />
            <rect x="82" y="14" width="14" height="7.5" rx="1" />
            <rect x="102" y="14" width="11" height="7.5" rx="1" />
            <rect x="116" y="14" width="10" height="7.5" rx="1" />
        </g>
        <rect x="98.5" y="12.5" width="2" height="21" fill="#D6DCE2" />
        <rect x="3" y="25" width="126" height="4.5" fill="#FF7722" />
        <rect x="3" y="29.5" width="126" height="4" fill={`url(#w4tb1-${uid})`} />
        <rect x="125" y="23" width="4" height="2.6" rx="1" fill="#FFE7A6" />
        <RoofFlag x={58} y={1} />
        {/* saffron + white pinstripes above the livery bands */}
        <g>
            <rect x="3" y="23.2" width="126" height="1.4" fill="#FF9933" />
            <rect x="3" y="24.6" width="126" height="1.4" fill="#FFFFFF" />
        </g>
        <ellipse cx="26" cy="33" rx="8.4" ry="3.4" fill="#1F1F1F" opacity=".18" />
        <ellipse cx="104" cy="33" rx="8.4" ry="3.4" fill="#1F1F1F" opacity=".18" />
        <Wheel cx={26} />
        <Wheel cx={104} />
        <Sun uid={uid} dur={8.1} delay={1.8} />
        <Tracker cx={66} y={6} dur={2.9} delay={0.82} />
    </svg>
);

const Car = ({ uid }) => (
    <svg width="76" height="38.7" viewBox="0 0 86 44" aria-hidden="true" focusable="false" style={VEHICLE_STYLE}>
        <Defs
            uid={uid}
            clip={
                <path d="M4 33.5 L4.5 26 Q5 22.5 10 21.5 L19 20.5 L27 12.5 Q29.5 10 34 10 L54 10 Q59 10 62 13 L69.5 20.5 L77 22 Q81 23 81 27 L81 33.5 Z" />
            }
        />
        <Shadow cx={43} rx={37} />
        <path
            d="M4 33.5 L4.5 26 Q5 22.5 10 21.5 L19 20.5 L27 12.5 Q29.5 10 34 10 L54 10 Q59 10 62 13 L69.5 20.5 L77 22 Q81 23 81 27 L81 33.5 Z"
            fill={`url(#w4tb4-${uid})`}
        />
        <path d="M4.2 27.5 L81 27.5 L81 33.5 L4 33.5 Z" fill="#245C99" />
        <path d="M29 20 L35.5 12.5 L43 12.5 L43 20 Z" fill={`url(#w4tg-${uid})`} />
        <path d="M46 12.5 L53.5 12.5 L60 20 L46 20 Z" fill={`url(#w4tg-${uid})`} />
        <rect x="44.3" y="12.5" width="1.4" height="7.5" fill="#245C99" />
        <path d="M10 22.4 Q32 19.4 60 20.4" stroke="#FFFFFF" strokeWidth="1" opacity=".3" fill="none" />
        <rect x="77" y="23.5" width="4" height="3" rx="1.2" fill="#FFE7A6" />
        <rect x="4" y="23.5" width="3.6" height="2.8" rx="1" fill="#E8564A" />
        {/* tricolour pinstripes along the doors */}
        <g>
            <rect x="7" y="24.4" width="70" height="1.2" fill="#FF9933" />
            <rect x="7" y="25.6" width="70" height="1.2" fill="#FFFFFF" />
            <rect x="7" y="26.8" width="70" height="1.2" fill="#138808" />
        </g>
        <ellipse cx="21" cy="33" rx="8" ry="3.2" fill="#1F1F1F" opacity=".2" />
        <ellipse cx="64" cy="33" rx="8" ry="3.2" fill="#1F1F1F" opacity=".2" />
        <Wheel cx={21} r={6.5} />
        <Wheel cx={64} r={6.5} />
        <Sun uid={uid} dur={9.4} delay={2.7} />
        <Tracker cx={45} y={8} dur={3.25} delay={1.23} />
    </svg>
);

const Ambulance = ({ uid }) => (
    <svg width="84" height="38.7" viewBox="0 0 96 44" aria-hidden="true" focusable="false" style={VEHICLE_STYLE}>
        <Defs uid={uid} clip={<path d="M4 33.5 L4 13 Q4 10.5 7 10.5 L60 10.5 L60 33.5 Z" />} />
        <Shadow cx={48} rx={42} />
        <path
            d="M4 33.5 L4 13 Q4 10.5 7 10.5 L60 10.5 L60 33.5 Z"
            fill={`url(#w4tb5-${uid})`}
            stroke="#D8DFE6"
            strokeWidth="1"
        />
        <path
            d="M60 10.5 L70 10.5 Q74 10.5 76.5 13.5 L83.5 21 L89 22.6 Q92.5 23.4 92.5 27 L92.5 33.5 L60 33.5 Z"
            fill={`url(#w4tb5-${uid})`}
            stroke="#D8DFE6"
            strokeWidth="1"
        />
        <path d="M70 13 L74.5 13 L80 20.5 L70 20.5 Z" fill={`url(#w4tg-${uid})`} />
        <rect x="61.5" y="13" width="7" height="7.5" rx="1" fill={`url(#w4tg-${uid})`} />
        <rect x="8" y="13.5" width="11" height="7" rx="1" fill={`url(#w4tg-${uid})`} opacity=".9" />
        <g fill="#E8564A">
            <rect x="28" y="15.4" width="11" height="3.6" rx=".6" />
            <rect x="31.7" y="11.7" width="3.6" height="11" rx=".6" />
        </g>
        <rect x="4" y="26" width="88.5" height="4" fill="#E8564A" />
        <rect x="4" y="30" width="88.5" height="1.8" fill="#1A3A8F" opacity=".45" />
        <rect x="36" y="6.6" width="18" height="4" rx="2" fill="#1A3A8F" />
        <rect x="38" y="7.4" width="5.5" height="2.4" rx="1.2" fill="#8CC8FF" />
        <rect x="46.5" y="7.4" width="5.5" height="2.4" rx="1.2" fill="#FFB3AC" />
        <rect x="88.5" y="24" width="4" height="3" rx="1.2" fill="#FFE7A6" />
        <ellipse cx="20" cy="33" rx="8.4" ry="3.4" fill="#1F1F1F" opacity=".16" />
        <ellipse cx="74" cy="33" rx="8.4" ry="3.4" fill="#1F1F1F" opacity=".16" />
        <Wheel cx={20} />
        <Wheel cx={74} />
        <Sun uid={uid} dur={5.5} delay={3.6} />
        <Tracker cx={25} y={8.5} dur={2.2} delay={1.64} />
    </svg>
);

/* Auto and Taxi: bodies verbatim; festive layers reconstructed — see the
 * RECONSTRUCTED SECTIONS note in the header comment. */

const Auto = ({ uid }) => (
    <svg width="53" height="38.7" viewBox="0 0 60 44" aria-hidden="true" focusable="false" style={VEHICLE_STYLE}>
        <Defs uid={uid} clip={<path d="M8 21 Q8 11 20 10 L38 10 Q46 11.5 48.5 18 L49 21 Z" />} />
        <Shadow cx={30} rx={25} />
        <path d="M8 21 Q8 11 20 10 L38 10 Q46 11.5 48.5 18 L49 21 Z" fill={`url(#w4tb6-${uid})`} />
        <path d="M11 15 L25 15 L25 21 L11 21 Z" fill="#2E3A44" opacity=".32" />
        <path d="M6 33.5 L6 24 Q6 21 10 21 L48 21 Q52 22 53 26 L53.5 33.5 Z" fill={`url(#w4tb7-${uid})`} />
        <circle cx="38" cy="17" r="3.2" fill="#3B2E2A" />
        <path d="M34.5 21 L34.5 19.4 Q38 17.4 41.5 19.4 L41.5 21 Z" fill="#F6F8F7" />
        <rect x="50.5" y="24.5" width="3" height="2.6" rx="1" fill="#FFE7A6" />
        <RoofFlag x={26} y={3} />
        <ellipse cx="17" cy="33" rx="7.2" ry="2.9" fill="#1F1F1F" opacity=".18" />
        <ellipse cx="48" cy="34.2" rx="6.2" ry="2.5" fill="#1F1F1F" opacity=".18" />
        <Wheel cx={17} r={6.5} hub={2.6} />
        <Wheel cx={48} cy={34.5} r={5.5} hub={2.2} />
        <Sun uid={uid} dur={6.8} delay={4.5} />
        <Tracker cx={15} y={8.8} dur={2.55} delay={2.05} />
    </svg>
);

const Taxi = ({ uid }) => (
    <svg width="77" height="38.7" viewBox="0 0 88 44" aria-hidden="true" focusable="false" style={VEHICLE_STYLE}>
        <Defs
            uid={uid}
            clip={
                <path d="M4 33.5 L4.5 26 Q5 22.5 10 21.5 L19 20.5 L27 12.5 Q29.5 10 34 10 L55 10 Q60 10 63 13 L70.5 20.5 L79 22 Q83 23 83 27 L83 33.5 Z" />
            }
        />
        <Shadow cx={44} rx={38} />
        <path
            d="M4 33.5 L4.5 26 Q5 22.5 10 21.5 L19 20.5 L27 12.5 Q29.5 10 34 10 L55 10 Q60 10 63 13 L70.5 20.5 L79 22 Q83 23 83 27 L83 33.5 Z"
            fill={`url(#w4tb6-${uid})`}
        />
        <path d="M4.2 27.5 L83 27.5 L83 33.5 L4 33.5 Z" fill="#1E2328" />
        <path d="M29 20 L35.5 12.5 L43.5 12.5 L43.5 20 Z" fill={`url(#w4tg-${uid})`} />
        <path d="M46.5 12.5 L54.5 12.5 L61 20 L46.5 20 Z" fill={`url(#w4tg-${uid})`} />
        <rect x="44.8" y="12.5" width="1.4" height="7.5" fill="#D6A81F" />
        <rect x="36" y="4.6" width="16" height="5" rx="1.4" fill="#1E2328" />
        <rect x="38" y="5.9" width="12" height="2.4" rx="1" fill="#F2C230" />
        <rect x="79" y="23.5" width="4" height="3" rx="1.2" fill="#FFE7A6" />
        <rect x="4" y="23.5" width="3.6" height="2.8" rx="1" fill="#E8564A" />
        {/* tricolour pinstripes above the black band */}
        <g>
            <rect x="8" y="23.9" width="68" height="1.2" fill="#FF9933" />
            <rect x="8" y="25.1" width="68" height="1.2" fill="#FFFFFF" />
            <rect x="8" y="26.3" width="68" height="1.2" fill="#138808" />
        </g>
        <ellipse cx="21" cy="33" rx="8" ry="3.2" fill="#1F1F1F" opacity=".2" />
        <ellipse cx="66" cy="33" rx="8" ry="3.2" fill="#1F1F1F" opacity=".2" />
        <Wheel cx={21} r={6.5} />
        <Wheel cx={66} r={6.5} />
        <Sun uid={uid} dur={8.1} delay={5.4} />
        <Tracker cx={30} y={8} dur={2.9} delay={2.46} />
    </svg>
);

/* -------------------------------------------------------------- scenery */

/* Static roadside: building silhouettes, a dome, trees and lamp poles, all
 * standing on the asphalt's top edge (y=32 of the 44-unit strip). Sliced
 * from the centre so narrow headers crop rather than distort. */
const Roadside = () => (
    <svg
        viewBox="0 0 640 44"
        preserveAspectRatio="xMidYMax slice"
        aria-hidden="true"
        focusable="false"
        style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0.5,
            pointerEvents: "none",
        }}
    >
        <g fill="#CBD6CE">
            <rect x="14" y="18" width="26" height="14" />
            <rect x="44" y="13" width="20" height="19" />
            <rect x="246" y="15" width="30" height="17" />
            <rect x="452" y="17" width="24" height="15" />
            <rect x="560" y="12" width="20" height="20" />
        </g>
        <g fill="#BDC9C0">
            <rect x="70" y="20" width="16" height="12" />
            <rect x="280" y="20" width="18" height="12" />
            <rect x="482" y="21" width="16" height="11" />
            <rect x="584" y="19" width="26" height="13" />
        </g>
        <path d="M330 32 L330 24 Q338 14 346 24 L346 32 Z" fill="#C4CFC6" />
        <g fill="#AFBDB2">
            <rect x="18" y="21" width="3" height="3" />
            <rect x="25" y="21" width="3" height="3" />
            <rect x="32" y="21" width="3" height="3" />
            <rect x="48" y="16" width="3" height="3" />
            <rect x="55" y="16" width="3" height="3" />
            <rect x="48" y="22" width="3" height="3" />
            <rect x="55" y="22" width="3" height="3" />
            <rect x="251" y="18" width="3" height="3" />
            <rect x="258" y="18" width="3" height="3" />
            <rect x="265" y="18" width="3" height="3" />
            <rect x="457" y="20" width="3" height="3" />
            <rect x="464" y="20" width="3" height="3" />
            <rect x="564" y="15" width="3" height="3" />
            <rect x="571" y="15" width="3" height="3" />
        </g>
        <g>
            <rect x="112" y="24" width="2.4" height="8" fill="#A08A70" />
            <circle cx="113.2" cy="21" r="6" fill="#9DBD93" />
            <rect x="180" y="26" width="2" height="6" fill="#A08A70" />
            <circle cx="181" cy="23.5" r="4.6" fill="#8FB487" />
            <rect x="392" y="24" width="2.4" height="8" fill="#A08A70" />
            <circle cx="393.2" cy="21" r="6" fill="#9DBD93" />
            <rect x="521" y="26" width="2" height="6" fill="#A08A70" />
            <circle cx="522" cy="23.5" r="4.6" fill="#8FB487" />
            <rect x="628" y="25" width="2.2" height="7" fill="#A08A70" />
            <circle cx="629.1" cy="21.8" r="5.2" fill="#9DBD93" />
        </g>
        <g>
            <rect x="216" y="14" width="1.6" height="18" fill="#9AA79A" />
            <rect x="216" y="14" width="4.5" height="1.2" fill="#9AA79A" />
            <circle cx="221.5" cy="15" r="1.7" fill="#E8E4D2" />
            <rect x="430" y="14" width="1.6" height="18" fill="#9AA79A" />
            <rect x="430" y="14" width="4.5" height="1.2" fill="#9AA79A" />
            <circle cx="435.5" cy="15" r="1.7" fill="#E8E4D2" />
        </g>
    </svg>
);

/* Saffron / white / green balloons: an outer drift and an independent
 * slower tilt, so no two trace the same path. */
const BALLOONS = [
    { left: "10%", top: 1, w: 10, colour: "#FF9933", float: "a", floatDur: 6.4, sway: "x", swayDur: 4.3, delay: 0 },
    { left: "38%", top: 3, w: 8, colour: "#F4F6F3", float: "b", floatDur: 7.6, sway: "y", swayDur: 5.2, delay: 1.4 },
    { left: "64%", top: 0, w: 9, colour: "#2F9E3F", float: "a", floatDur: 8.3, sway: "y", swayDur: 4.8, delay: 2.2 },
    { left: "88%", top: 2, w: 8, colour: "#FF9933", float: "b", floatDur: 7.1, sway: "x", swayDur: 5.6, delay: 0.8 },
];

const Balloons = () => (
    <div
        aria-hidden="true"
        style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", opacity: 0.7 }}
    >
        {BALLOONS.map((b, i) => (
            <span
                key={i}
                style={{
                    position: "absolute",
                    left: b.left,
                    top: b.top,
                    animation: `w4t-float-${b.float} ${b.floatDur}s ease-in-out ${b.delay}s infinite`,
                }}
            >
                <span style={{ display: "block", animation: `w4t-sway-${b.sway} ${b.swayDur}s ease-in-out infinite` }}>
                    <svg
                        viewBox="0 0 12 19"
                        width={b.w}
                        height={(b.w * 19) / 12}
                        aria-hidden="true"
                        focusable="false"
                        style={{ display: "block" }}
                    >
                        <ellipse cx="6" cy="5.6" rx="4.6" ry="5.4" fill={b.colour} />
                        <ellipse cx="4.4" cy="3.8" rx="1.4" ry="2" fill="#FFFFFF" opacity=".35" />
                        <path d="M5.2 10.6 L6.8 10.6 L6 12 Z" fill={b.colour} />
                        <path d="M6 12 Q4.8 15 6.2 18" fill="none" stroke="#8D97A3" strokeWidth=".5" />
                    </svg>
                </span>
            </span>
        ))}
    </div>
);

/* Marigold petals drifting down across the whole strip. */
const PETALS = [
    { left: "6%", dur: 6.6, delay: 0, colour: "#FF9933" },
    { left: "22%", dur: 7.8, delay: 2.1, colour: "#F2A03D" },
    { left: "41%", dur: 5.9, delay: 3.4, colour: "#FFC46B" },
    { left: "58%", dur: 8.4, delay: 1.2, colour: "#FF9933" },
    { left: "76%", dur: 6.2, delay: 4.3, colour: "#F2A03D" },
    { left: "92%", dur: 7.2, delay: 2.8, colour: "#FFC46B" },
];

const Petals = () => (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        {PETALS.map((p, i) => (
            <span
                key={i}
                style={{
                    position: "absolute",
                    left: p.left,
                    top: -6,
                    opacity: 0,
                    animation: `w4t-petal ${p.dur}s linear ${p.delay}s infinite`,
                }}
            >
                <svg viewBox="0 0 6 4" width="5" height="3.4" aria-hidden="true" focusable="false" style={{ display: "block" }}>
                    <ellipse cx="3" cy="2" rx="2.8" ry="1.7" fill={p.colour} />
                </svg>
            </span>
        ))}
    </div>
);

/* ----------------------------------------------------------------- slot */

const VEHICLES = [Truck, Bike, Bus, Car, Ambulance, Auto, Taxi];
const BOB = ["a", "b", "c", "b", "a", "c", "b"];
const BOB_DUR = { a: 1.9, b: 2.35, c: 2.7 };
const SMOKE = [[0, 2.6, 2.0], [0.85, 2.2, 2.6], [1.7, 2.9, 1.7]]; // [phase offset, duration, radius]
const DUST_DUR = [1.5, 1.78, 2.06, 2.34];

/* Everything the vehicle trails or throws lives on the slot, BEFORE the
 * <svg>, so the bodywork occludes it and the smoke reads as coming out from
 * behind. The vehicle is mirrored and travels left, so its front is the
 * slot's LEFT edge and its tailpipe the RIGHT. */
function Slot({ V, i }) {
    const v = BOB[i % 7];
    const uid = `v${i}`; // gradient/clip ids must be unique per instance
    const bobDelay = ((i * 0.37 + Math.floor(i / 7) * 0.19) % 1.4).toFixed(2);
    const lampPhase = ((i * 0.53) % 3).toFixed(2);
    const smokeBase = (i * 0.37) % 2.2;
    const crumbBase = (i * 0.29) % 1.2;
    const dustDur = DUST_DUR[i % 4];
    const dustDelay = ((i * 0.31) % 1.5).toFixed(2);

    return (
        <span
            className="w4t-convoy__slot"
            style={{ animation: `w4t-bob-${v} ${BOB_DUR[v]}s ease-in-out ${bobDelay}s infinite` }}
        >
            {/* headlight cone — tapered, thrown forward off the nose */}
            <span
                style={{
                    position: "absolute",
                    left: -15,
                    bottom: 3,
                    width: 17,
                    height: 10,
                    pointerEvents: "none",
                    clipPath: "polygon(0 18%, 100% 0, 100% 100%, 0 82%)",
                    background:
                        "linear-gradient(270deg, rgba(255,226,150,.6), rgba(255,232,170,.16) 52%, rgba(255,236,190,0))",
                    animation: `w4t-lamp 3.4s ease-in-out ${lampPhase}s infinite`,
                }}
            />
            {/* tail lamp */}
            <span
                style={{
                    position: "absolute",
                    right: -3,
                    bottom: 5,
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    pointerEvents: "none",
                    background:
                        "radial-gradient(circle, rgba(232,86,74,.6), rgba(232,86,74,.18) 55%, rgba(232,86,74,0))",
                    animation: `w4t-tail 2.6s ease-in-out ${lampPhase}s infinite`,
                }}
            />
            {/* GPS breadcrumb trail — the route already logged */}
            <span style={{ position: "absolute", right: -46, bottom: 8, width: 46, height: 6, pointerEvents: "none" }}>
                {[0, 1, 2, 3, 4].map((k) => (
                    <span
                        key={k}
                        style={{
                            position: "absolute",
                            left: 4 + k * 9,
                            bottom: 1.4 + k * 0.2,
                            width: 3 - k * 0.32,
                            height: 3 - k * 0.32,
                            borderRadius: "50%",
                            background: "#4FAE43",
                            opacity: 0,
                            animation: `w4t-crumb 2.4s linear ${(crumbBase + k * 0.16).toFixed(2)}s infinite`,
                        }}
                    />
                ))}
            </span>
            {/* exhaust — pinned to the container's RIGHT edge, i.e. the tailpipe */}
            <span style={{ position: "absolute", right: -4, bottom: 2, width: 30, height: 16, pointerEvents: "none" }}>
                {SMOKE.map(([off, dur, r], k) => (
                    <span
                        key={k}
                        style={{
                            position: "absolute",
                            right: 0,
                            bottom: 0,
                            width: r * 2,
                            height: r * 2,
                            borderRadius: "50%",
                            opacity: 0,
                            background:
                                "radial-gradient(circle at 40% 40%, rgba(226,226,226,.85), rgba(196,198,196,.5) 60%, rgba(190,192,190,0) 100%)",
                            animation: `w4t-smoke ${dur}s ease-out ${(smokeBase + off).toFixed(2)}s infinite`,
                        }}
                    />
                ))}
            </span>
            {/* dust off the rear tyre */}
            <span
                style={{
                    position: "absolute",
                    right: -6,
                    bottom: 1,
                    width: 22,
                    height: 9,
                    pointerEvents: "none",
                    animation: `w4t-dust ${dustDur}s ease-out ${dustDelay}s infinite`,
                }}
            >
                <svg viewBox="0 0 22 9" width="22" height="9" aria-hidden="true" focusable="false" style={{ display: "block" }}>
                    <ellipse cx="6" cy="6.4" rx="5.4" ry="2.5" fill="#CBBFA8" opacity=".5" />
                    <ellipse cx="13" cy="5" rx="4" ry="2" fill="#D6CCB8" opacity=".38" />
                    <ellipse cx="18.5" cy="4" rx="2.8" ry="1.5" fill="#E0D8C8" opacity=".26" />
                </svg>
            </span>
            <V uid={uid} />
        </span>
    );
}

/* ----------------------------------------------------------------- root */

export default function HeaderConvoy({ seconds = 26 }) {
    const set = (pass) => VEHICLES.map((V, i) => <Slot key={`${pass}-${i}`} V={V} i={pass * 7 + i} />);

    /* One ground speed — 2691px per `seconds` — drives the travel and every
     * wheel. --w4t-convoy-set feeds the shared w4t-convoy keyframe, giving
     * exactly the brief's -2691px translate (deviation 1 in the CSS). */
    const track = {
        "--w4t-convoy-set": `${SET}px`,
        "--w4t-convoy-duration": `${seconds}s`,
        "--w4t-wheel-a": `${(((2 * Math.PI * 6.8 * SCALE) / SET) * seconds).toFixed(3)}s`,
        "--w4t-wheel-b": `${(((2 * Math.PI * 6.5 * SCALE) / SET) * seconds).toFixed(3)}s`,
        "--w4t-wheel-c": `${(((2 * Math.PI * 5.5 * SCALE) / SET) * seconds).toFixed(3)}s`,
    };

    /* Paint order is load-bearing — several earlier bugs were nothing but
     * wrong ordering. Scenery, balloons, verge, OPAQUE asphalt, kerb, static
     * dashes, tricolour line, golden-hour light (after the asphalt or it is
     * invisible), petals, the track, and the mask does the edge fading. */
    return (
        <div className="w4t-convoy" aria-hidden="true">
            <Roadside />
            <Balloons />
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 12,
                    height: 2,
                    background: "linear-gradient(180deg,#DCE4DD,#CBD5CC)",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 12,
                    background: "linear-gradient(180deg,#5B6167 0%,#4C5257 42%,#43484D 100%)",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 11,
                    height: 1,
                    background: "rgba(255,255,255,.28)",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 5.5,
                    height: 1.5,
                    background:
                        "repeating-linear-gradient(90deg, rgba(255,255,255,.62) 0 13px, rgba(255,255,255,0) 13px 34px)",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 1.5,
                    opacity: 0.85,
                    background: "linear-gradient(90deg,#138808,#F2F4F1 50%,#FF9933)",
                    pointerEvents: "none",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background:
                        "linear-gradient(180deg, rgba(255,214,150,.22) 0%, rgba(255,236,205,.1) 46%, rgba(255,255,255,0) 70%)",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 12,
                    pointerEvents: "none",
                    background:
                        "linear-gradient(90deg, rgba(255,178,74,.22), rgba(255,255,255,0) 34%, rgba(255,255,255,0) 66%, rgba(255,178,74,.18))",
                }}
            />
            <Petals />
            <div className="w4t-convoy__track" style={track}>
                {set(0)}
                {set(1)}
            </div>
        </div>
    );
}
